import {
  Box,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
  type BoxProps,
  type SimpleGridProps,
} from "@chakra-ui/react";
import { forwardRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { SeeTheListButton } from "./SeeTheListButton";
import { InPageDetails, useIsTouchScreen, useTouchDetails } from "./TouchDetails";

type FigureTileProps = {
  label: string;
  shown: string;
  below?: ReactNode;
  definition: string;
  details: ReactNode;
  link: string;
  testId: string;
};

export const OverviewFigureGrid = ({ columns, children }: Pick<SimpleGridProps, "columns" | "children">) => (
  <SimpleGrid columns={columns} spacing="4" gridAutoFlow="row dense">
    {children}
  </SimpleGrid>
);

export const OverviewFigureTile = (props: FigureTileProps) => {
  return useIsTouchScreen() ? <TouchFigureTile {...props} /> : <HoverFigureTile {...props} />;
};

const HoverFigureTile = ({ label, shown, below, definition, details, link, testId }: FigureTileProps) => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const openList = () => {
    onClose();
    navigate(link);
  };

  return (
    <Popover trigger="hover" placement="bottom-start" isLazy openDelay={150} isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <FigureButton label={label} shown={shown} below={below} onClick={openList} data-testid={`figure-${testId}`} />
      </PopoverTrigger>
      <PopoverContent maxW="xs" bg="smBackground" borderColor="smBorder" data-testid={`figure-details-${testId}`}>
        <PopoverArrow bg="smBackground" />
        <PopoverBody fontSize="sm">
          <FigureDetails definition={definition} details={details} link={link} onLeave={onClose} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const TouchFigureTile = ({ label, shown, below, definition, details, link, testId }: FigureTileProps) => {
  const { regionRef, openKey, close, triggerProps, detailsId } = useTouchDetails<string>();

  return (
    <Box ref={regionRef} display="contents">
      <FigureButton label={label} shown={shown} below={below} {...triggerProps(testId)} data-testid={`figure-${testId}`} />
      {openKey === testId && (
        <InPageDetails id={detailsId} gridColumn="1 / -1" data-testid={`figure-details-${testId}`}>
          <FigureDetails definition={definition} details={details} link={link} onLeave={close} />
        </InPageDetails>
      )}
    </Box>
  );
};

const FigureButton = forwardRef<HTMLButtonElement, BoxProps & { label: string; shown: string; below?: ReactNode }>(
  function FigureButton({ label, shown, below, ...rest }, ref) {
    return (
      <Box
        ref={ref}
        as="button"
        type="button"
        textAlign="left"
        bg="smBackground"
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        p="4"
        _hover={{ borderColor: "smPrimary" }}
        _focusVisible={{ boxShadow: "outline" }}
        {...rest}
      >
        <Text as="span" fontSize="sm" color="smMuted" borderBottom="1px dotted" borderColor="smBorder">
          {label}
        </Text>
        <Text fontSize="2xl" fontWeight="semibold" color="smText" mt="1">
          {shown}
        </Text>
        {below}
      </Box>
    );
  },
);

const FigureDetails = ({
  definition,
  details,
  link,
  onLeave,
}: {
  definition: string;
  details: ReactNode;
  link: string;
  onLeave: () => void;
}) => (
  <Stack spacing="2">
    <Text color="smText">{definition}</Text>
    <Box color="smMuted">{details}</Box>
    <SeeTheListButton to={link} onClick={onLeave} />
  </Stack>
);
